/**
 * MCP SDK Types - Shared type definitions for MCP components
 * 
 * Zero hardcoded values - all visual theming uses CSS variables
 */

// ============================================
// INSTANCE METADATA
// ============================================

/**
 * Instance-level metadata for system-backed tools.
 * Surfaces WHERE the tool operates (which tenant/instance).
 */
export interface ToolInstanceMetadata {
  id: string;
  label: string;
  env: string;
  host: string;
  auth_type: string;
  system_type: string;
  base_url?: string;
}

// ============================================
// ENVIRONMENT & STYLING
// ============================================

export interface EnvStyle {
  bg: string;
  text: string;
  border: string;
  icon: string;
}

const PROD_STYLE: EnvStyle = {
  bg: 'var(--error-bg, rgba(239, 68, 68, 0.12))',
  text: 'var(--error-text, #ef4444)',
  border: 'var(--error-border, rgba(239, 68, 68, 0.3))',
  icon: '\u{1F534}',
};

const DEV_STYLE: EnvStyle = {
  bg: 'var(--success-bg, rgba(16, 185, 129, 0.12))',
  text: 'var(--success-text, #10b981)',
  border: 'var(--success-border, rgba(16, 185, 129, 0.3))',
  icon: '\u{1F7E2}',
};

const UNKNOWN_STYLE: EnvStyle = {
  bg: 'var(--card-bg, rgba(128, 128, 128, 0.12))',
  text: 'var(--main-text-secondary, rgba(255, 255, 255, 0.6))',
  border: 'var(--border-color, rgba(255, 255, 255, 0.1))',
  icon: '\u{2753}',
};

export const ENV_STYLES: Record<string, EnvStyle> = {
  prod: PROD_STYLE,
  production: PROD_STYLE,
  staging: {
    bg: 'var(--warning-bg, rgba(245, 158, 11, 0.12))',
    text: 'var(--warning-text, #f59e0b)',
    border: 'var(--warning-border, rgba(245, 158, 11, 0.3))',
    icon: '\u{1F7E1}',
  },
  demo: {
    bg: 'var(--info-bg, rgba(59, 130, 246, 0.12))',
    text: 'var(--info-text, #3b82f6)',
    border: 'var(--info-border, rgba(59, 130, 246, 0.3))',
    icon: '\u{1F535}',
  },
  dev: DEV_STYLE,
  development: DEV_STYLE,
  unknown: UNKNOWN_STYLE,
};

export function getEnvStyle(env: string): EnvStyle {
  return ENV_STYLES[env.toLowerCase()] || ENV_STYLES.unknown;
}

export function isProdEnv(env: string): boolean {
  const lowerEnv = env.toLowerCase();
  return lowerEnv === 'prod' || lowerEnv === 'production';
}

/**
 * Production warning state for tool selection footer.
 */
export interface ProdWarningState {
  hasProdSelection: boolean;
  prodInstances: string[];
  prodToolCount: number;
  totalToolCount: number;
}

// ============================================
// OPERATION TYPE HELPERS
// ============================================

export type OperationType = 'read' | 'write' | 'delete' | 'other';

export function getOperationType(toolName: string): OperationType {
  const lowerName = toolName.toLowerCase();

  if (lowerName.includes('delete') || lowerName.includes('remove') || lowerName.includes('revoke')) {
    return 'delete';
  }
  if (lowerName.includes('create') || lowerName.includes('update') ||
      lowerName.includes('set') || lowerName.includes('add') ||
      lowerName.includes('modify') || lowerName.includes('enable') ||
      lowerName.includes('disable') || lowerName.includes('assign')) {
    return 'write';
  }
  if (lowerName.includes('get') || lowerName.includes('list') ||
      lowerName.includes('read') || lowerName.includes('search') ||
      lowerName.includes('find') || lowerName.includes('query') ||
      lowerName.includes('count') || lowerName.includes('check') ||
      lowerName.includes('view') || lowerName.includes('by_')) {
    return 'read';
  }
  return 'other';
}

export function getOperationStyle(opType: OperationType): { icon: string; color: string; bg: string } {
  switch (opType) {
    case 'delete':
      return { icon: '\u{1F5D1}\u{FE0F}', color: 'var(--error-text, #ef4444)', bg: 'var(--error-bg, rgba(239, 68, 68, 0.12))' };
    case 'write':
      return { icon: '\u{270F}\u{FE0F}', color: 'var(--warning-text, #f59e0b)', bg: 'var(--warning-bg, rgba(245, 158, 11, 0.12))' };
    case 'read':
      return { icon: '\u{1F4D6}', color: 'var(--success-text, #10b981)', bg: 'var(--success-bg, rgba(16, 185, 129, 0.12))' };
    default:
      return { icon: '\u{1F527}', color: 'var(--main-text-secondary)', bg: 'var(--card-bg, rgba(128, 128, 128, 0.12))' };
  }
}

/**
 * Virtual Server - A curated bundle of tools for specific agent use cases
 */
export interface VirtualServer {
  id: string;
  name: string;
  display_name?: string;   // Human-readable name
  description: string;
  parent_service_id: string;
  parent_server_id?: string; // Alias for parent_service_id (API compat)
  parent_service_name?: string;
  icon?: string;           // Emoji or icon URL
  tool_count: number;
  tool_ids?: string[];     // Tool IDs included in this VS
  order?: number;          // Display order
  recommended_for?: string; // "Support agents, DevOps teams"
  created_at?: string;
  updated_at?: string;
  // Filter-based assignment
  filters?: {
    source?: string[];
    tags_include?: string[];
    provider?: string[];
  };
}

/**
 * Tool parameter definition
 */
export interface ToolParameter {
  name: string;
  type: string;
  description?: string;
  required: boolean;
  default?: unknown;
  enum?: string[];
}

/**
 * Tool metadata - additional context about the tool
 */
export interface ToolMetadata {
  backend_type: 'command' | 'workflow' | 'function';
  system?: string;
  object_type?: string;
  action?: string;
  workflow_name?: string;
  provider?: string;
  instance?: string;
  virtual_server_id?: string;
  virtual_server_name?: string;
  source?: string;  // e.g., "builtin", "workflow", "system"
}

/**
 * Tool annotations - MCP annotations dict for filter matching and UI hints
 */
export interface ToolAnnotations {
  tags?: string[];
  provider?: string;
  instance?: string;
  deprecated?: boolean;
  category?: string;
  [key: string]: unknown;
}

/**
 * MCP Tool - A callable capability from an MCP service
 * 
 * Normalized Fields (from backend API):
 * - `tags`: Flat list at top level for virtual server filter matching
 * - `source`: Tool source type (system, workflow, builtin, agent) at top level
 * - `annotations`: Full MCP annotations dict at top level
 * 
 * These fields are normalized by the backend API for consistent access.
 * The metadata object also contains source/provider for backward compatibility.
 */
export interface MCPTool {
  id: string;
  name: string;
  description: string;
  version?: string;
  service_id: string;
  service_name?: string;
  service_display_name?: string;  // Added by bulk endpoint
  categories?: string[];
  
  // Normalized fields (populated by backend normalizer)
  tags?: string[];                // Consolidated tags from annotations.tags + categories
  source?: string;                // Tool source: "system" | "workflow" | "builtin" | "agent"
  annotations?: ToolAnnotations;  // Full MCP annotations dict
  
  parameters?: ToolParameter[];
  metadata?: ToolMetadata;
  enabled?: boolean;
  inputSchema?: Record<string, unknown>;
  
  // Virtual server context (populated when filtered by VS)
  virtual_server_id?: string;
  virtual_server_name?: string;

  // Instance metadata (for system-backed tools with tenant/env context)
  instance?: ToolInstanceMetadata;
}

/**
 * Tool selection state for workflow components
 * 
 * NOTE: service_id, service_name, service_display_name are REQUIRED for
 * workflow compatibility - ToolSelector.tsx uses these to compute
 * primary_mcp_service_id which links the agent to its MCP service.
 */
export interface ToolSelectionState {
  selectedToolIds: string[];
  selectedTools: Array<{
    id: string;
    name: string;
    description: string;
    // Service origin - required for primary_mcp_service_id calculation
    service_id?: string;
    service_name?: string;
    service_display_name?: string;
  }>;
}

/**
 * Tool browser filter state
 */
export interface ToolFilterState {
  search: string;
  virtualServerId: string | null;
  category: string | null;
  backendType: string | null;
  provider: string | null;
}

/**
 * API response types
 */
export interface VirtualServersResponse {
  virtual_servers: VirtualServer[];
  total: number;
}

export interface ToolsResponse {
  tools: MCPTool[];
  total: number;
}

